import { Inject, Injectable } from '@angular/core';
import { find, get } from 'lodash';
import { BehaviorSubject, Observable, map, of } from 'rxjs';

import {
  PortalConfigDefinition,
  PortalFormConfig,
} from '@lockton-apps/shared/interfaces';

@Injectable({
  providedIn: 'root',
})
export class ConfigResolverService {
  private readonly config = new BehaviorSubject<PortalConfigDefinition | null>(
    null
  );

  constructor(
    //feature:portalConfig-integration remove all instances in the file
    @Inject('FEATURE_PORTALCONFIG') readonly isFeatureEnabled: boolean
  ) {}

  initialiseConfig(config: PortalConfigDefinition) {
    this.config.next(config);
  }

  resolveLabelName(
    labelId: string,
    portalId: number | null = null
  ): Observable<string | null> {
    if (!this.isFeatureEnabled) {
      return of(null);
    }
    return this.config
      .asObservable()
      .pipe(
        map(() =>
          portalId
            ? this.getForPortalId(portalId, `labels.${labelId}`) ?? null
            : null
        )
      );
  }

  resolveFormConfig(
    portalId: number,
    formId: string
  ): Observable<PortalFormConfig | null | undefined> {
    if (!this.isFeatureEnabled) {
      return of(null);
    }

    return this.config
      .asObservable()
      .pipe(map((config) => get(config, `${portalId}.formConfig.${formId}`)));
  }

  resolveInputDefaultValue(
    inputId: string,
    portalId: number | null = null
  ): Observable<string | null> {
    if (!this.isFeatureEnabled) {
      return of(null);
    }
    return this.config
      .asObservable()
      .pipe(
        map(() =>
          portalId
            ? this.getForPortalId(portalId, `inputs.${inputId}.defaultValue`) ??
              null
            : null
        )
      );
  }

  resolveOtherProperty<T>(
    portalId: number | null,
    propertyPath: string
  ): Observable<T | null> {
    return this.config
      .asObservable()
      .pipe(
        map(() =>
          portalId
            ? this.getForPortalId(portalId, `other.${propertyPath}`) ?? null
            : null
        )
      );
  }

  private getForPortalId(portalId: number, path: string) {
    return get(this.config.value, `[${portalId}].${path}`);
  }
}
